import { Button, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { format } from 'date-fns-tz';
import { Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CheckboxSelectAsync from 'components/CheckboxSelectAsync';
import { FormikDateRangePicker, FormikSelect, FormikSelectAsyncPaginate } from 'components/Formik';
import Title from 'components/Title';
import { fetchPaginatedEmployee } from 'hooks/employee/useFetchPaginatedEmployee';
import { fetchPaginatedJobs } from 'hooks/jobs/useFetchPaginatedJobs';
import { useFetchSegmentedTrackingReports } from 'hooks/reports/useFetchSegmentedTrackingReports';
import { useFetchWeekIntervals } from 'hooks/reports/useFetchWeekIntervals';
import { useCompanyInfo } from 'store/useUserStore';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import {
  initialSegmentedTrackingReportsData,
  segmentedReportsDateRangeDataValidationSchema,
  segmentedReportsPayPeriodDataValidationSchema,
} from './schema';
import SegmentedTrackingReportTable from './SegmentedTrackingReportTable';

const SegmentedTrackingReport: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'reports']);
  const formikRef = useRef<any>();

  const companyInfo = useCompanyInfo();

  const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[] | null>([]);
  const [segmentedTrackingReport, setSegmentedTrackingReport] = useState<any>();

  const { status: reportStatus, mutateAsync } = useFetchSegmentedTrackingReports();

  const [enableFetchWeekInterval, setEnableFetchWeekInterval] = useState<boolean>(false);

  const [useWeeklyDateRange, setUseWeeklyDateRange] = useState<boolean>(false);

  const { data: weeklyData, status: fetchWeekIntervalsStatus } = useFetchWeekIntervals({
    enabled: enableFetchWeekInterval,
  });

  const handleFormSubmit = (values: typeof initialSegmentedTrackingReportsData) => {
    const jobIds = Array.isArray(selectedJobs) ? selectedJobs.map((job: any) => job.id).flat() : [];
    let startDate, endDate;

    if (values.type?.value == 'byJob' && useWeeklyDateRange) {
      startDate = values.pay_period?.first ? format(new Date(values.pay_period.first), 'MM-dd-yyyy') : '';
      endDate = values.pay_period?.last ? format(new Date(values.pay_period.last), 'MM-dd-yyyy') : '';
    } else {
      startDate = format(values.date_range[0], 'MM-dd-yyyy');
      endDate = format(values.date_range[1], 'MM-dd-yyyy');
    }

    const postData = {
      start_date: startDate,
      end_date: endDate,
      type: values.type?.value || 'all',
      all_jobs: Array.isArray(jobIds) ? jobIds.length === 0 : true,
      job_ids: jobIds || [],
      user_ids: Array.isArray(selectedUsers) && selectedUsers.length ? selectedUsers.map(user => user.userId) : [],
    };

    mutateAsync(postData).then(data => {
      setSegmentedTrackingReport(data?.payload);
    });
  };

  const segmentedReportsTypeOptions: ILabelValueOptions[] = [
    { label: 'All', value: 'all' },
    { label: 'By Job', value: 'byJob' },
    { label: 'By Mileage', value: 'byMileage' },
  ];

  const jobLoadOptions = async (search: any, page: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedJobs, { search, page, job_status: 1 });
  };

  const employeeLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedEmployee, {
      page,
      search,
      active: '1',
      report: true,
    });
  };

  return (
    <div>
      <Title>{t('reports:segmentedTrackingReports')}</Title>
      <Flex mt="5" justifyContent="center">
        <Formik
          initialValues={initialSegmentedTrackingReportsData}
          validationSchema={
            useWeeklyDateRange
              ? segmentedReportsPayPeriodDataValidationSchema
              : segmentedReportsDateRangeDataValidationSchema
          }
          onSubmit={handleFormSubmit}
        >
          {formikBag => {
            formikRef.current = formikBag;
            const { setFieldValue } = formikBag;
            return (
              <Form autoComplete="off" style={{ width: '100%' }}>
                <Grid
                  display="inline-grid"
                  justifyContent="center"
                  templateColumns={{ base: '1fr', sm: '1fr 1fr', lg: '300px 300px', xl: '400px 400px' }}
                  gap={{ base: 0, sm: 4 }}
                  w="100%"
                >
                  <GridItem>
                    <FormikSelectAsyncPaginate
                      name="user_ids"
                      label={t('common:selectUser')}
                      loadOptions={employeeLoadOptions}
                      onChangeCallback={(data: any) => {
                        setSelectedUsers(data);
                      }}
                      selectProps={{
                        isMulti: true,
                        placeholder: t('common:allUsers'),
                        getOptionLabel: (option: any) => option?.firstName + ' ' + option?.lastName,
                        getOptionValue: (option: any) => option.userId,
                      }}
                    />
                  </GridItem>
                  <GridItem>
                    <Text color="gray.500" fontSize="12px" fontWeight="600" mb="1" textTransform="uppercase">
                      {t('common:selectJob')}
                    </Text>
                    <CheckboxSelectAsync
                      loadOptions={(search: any, _: any, { page }: any) => jobLoadOptions(search, page)}
                      optionSelected={selectedJobs}
                      handleOptionChange={(selected: any) => {
                        setSelectedJobs(selected);
                        if (Array.isArray(selected) && selected.length > 0) {
                          setFieldValue('type', segmentedReportsTypeOptions[1]);
                        }
                      }}
                      selectProps={{
                        placeholder: t('common:allJobs'),
                        getOptionLabel: (option: any) => option.name,
                        getOptionValue: (option: any) => option.id,
                        size: 'lg',
                        variant: 'filled',
                        fontSize: 'sm',
                        maxToShow: 1,
                      }}
                    />
                  </GridItem>
                  <GridItem>
                    <FormikSelect
                      label={t('common:type')}
                      name="type"
                      options={segmentedReportsTypeOptions}
                      onChangeCallback={(option: any) => {
                        if (option?.value !== 'byJob') {
                          setSelectedJobs([]);
                          setUseWeeklyDateRange(false);
                        } else {
                          setUseWeeklyDateRange(companyInfo?.includeOvertimeInSegmented);
                        }
                      }}
                    />
                  </GridItem>
                  <GridItem>
                    {useWeeklyDateRange ? (
                      <FormikSelect
                        options={weeklyData?.payload || []}
                        label={t('reports:payPeriod')}
                        name="pay_period"
                        selectProps={{
                          isLoading: fetchWeekIntervalsStatus === 'loading',
                          onMenuOpen: () => setEnableFetchWeekInterval(true),
                          getOptionLabel: (option: any) => option?.toShow,
                          getOptionValue: (option: any) => option?.toShow,
                          placeholder: t('reports:selectPayPeriod'),
                          isClearable: false,
                        }}
                      />
                    ) : (
                      <FormikDateRangePicker label={t('common:dateRange')} name="date_range" />
                    )}
                  </GridItem>
                  <GridItem justifySelf="flex-end" colStart={2}>
                    <Button type="submit" colorScheme="secondary" isLoading={reportStatus === 'loading'}>
                      {t('reports:generateReport')}
                    </Button>
                  </GridItem>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Flex>
      {segmentedTrackingReport ? (
        <SegmentedTrackingReportTable
          data={segmentedTrackingReport}
          dateRange={
            useWeeklyDateRange
              ? [
                  new Date(formikRef?.current?.values?.pay_period?.first),
                  new Date(formikRef?.current?.values?.pay_period?.last),
                ]
              : formikRef?.current?.values?.date_range
          }
          companyInfo={companyInfo}
        />
      ) : null}
    </div>
  );
};

export default SegmentedTrackingReport;
